<div id="printContent">
  <wf-page-tour [steps]="secureFileSharingIntroSteps"></wf-page-tour>
  <div class="header-controls split-flex">
    <h3>
      <fa-icon [icon]="filesIcon"/>
      Secure Data Sharing
    </h3>
  </div>
  <div class="report-section-header"></div>
  <div class="boxed-content">
    <!-- File List Table -->
    @if (files.length > 0) {
      <div class="file-table">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <!-- Clickable header for File Name -->
              <th (click)="sortBy('fileName')" style="cursor:pointer;">
                File Name
                @if (sortColumn==='fileName') {
                  <fa-icon [icon]="sortDirection==='asc' ? faChevronDown : faChevronRight"></fa-icon>
                }
              </th>
              <!-- Clickable header for Upload Date -->
              <th (click)="sortBy('uploadDate')" style="cursor:pointer;">
                Upload Date
                @if (sortColumn==='uploadDate') {
                  <fa-icon [icon]="sortDirection==='asc' ? faChevronDown : faChevronRight"></fa-icon>
                }
              </th>
              <!-- Clickable header for Expiration Date -->
              <th (click)="sortBy('expirationDate')" style="cursor:pointer;">
                Expiration Date
                @if (sortColumn==='expirationDate') {
                  <fa-icon [icon]="sortDirection==='asc' ? faChevronDown : faChevronRight"></fa-icon>
                }
              </th>
              <th>File Size</th>
              <th>Owner</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <!-- Search Bar Row -->
            @if (managementUser?.isCustomerServiceUser()) {
              <tr class="search-row">
                <td colspan="7">
                  <input
                    type="text"
                    placeholder="Search..."
                    [(ngModel)]="searchTerm"
                    class="search-bar" />
                </td>
              </tr>
            }
            <!-- File Rows -->
            @for (file of sortedFilteredFiles; track file; let i = $index) {
              <!-- Main Row -->
              <tr class="main-row" (click)="toggleDetails(file.fileID || i)">
                <td class="id-column">{{ i + 1 }}</td>
                <td class="file-name-column">
                  <!-- File expansion indicator icon -->
                  <fa-icon [icon]="file.showDetails ? faChevronDown : faChevronRight" style="margin-right:5px;"></fa-icon>
                  {{ file.fileName }}
                </td>
                <td class="upload-date-column">
                  {{ file.uploadDate ? (file.uploadDate | date:'shortDate') : '-' }}
                </td>
                <td class="expiration-date-column">
                  {{ file.expirationDate ? (file.expirationDate | date:'shortDate') : '-' }}
                </td>
                <td class="file-size-column">{{ formatFileSize(file.fileSize) }}</td>
                <td class="owner-column">
                  {{ file.ownerUserID === managementUser?.userID ? 'You' : file.ownerUser!.firstName + ' ' + file.ownerUser!.lastName }}
                </td>
                <td class="actions-column">
                  <!-- Conditionally render Share and Remove buttons only for the owner -->
                  @if (file.ownerUserID === managementUser?.userID) {
                    @if (managementUser?.isCustomerServiceUser() || managementUser?.isPartnerSuccessUser()) {
                      <button class="action-share-button" (click)="shareAction(file.fileID || i); $event.stopPropagation()">Share</button>
                    }
                    <button (click)="removeFile(file.fileID!); $event.stopPropagation()">Remove</button>
                  }
                  <!-- Download button available to all -->
                  <button class="download-button" (click)="downloadFile(file); $event.stopPropagation()">Download</button>
                </td>
              </tr>
              <!-- Detail Row -->
              @if (file.showDetails) {
                <tr class="detail-row">
                  <td colspan="7">
                    <div class="detail-content">
                      <div><strong>Owner:</strong> {{ file.ownerUser!.firstName }} {{ file.ownerUser!.lastName }}</div>
                      <div><strong>Email:</strong> {{ file.ownerUser!.email }}</div>
                      <div><strong>District:</strong> {{ file.ownerUser!.districtName }}</div>
                      <div><strong>School:</strong> {{ file.ownerUser!.school ? file.ownerUser!.school : 'N/A' }}</div>
                      <div><strong>Type:</strong> {{ file.fileType }}</div>
                      <div><strong>Size:</strong> {{ formatFileSize(file.fileSize) }}</div>
                      @if (file.description) {
                        <div><strong>Note:</strong> {{ file.description }}</div>
                      }
                      <!-- Share Section (only visible to the owner) -->
                      @if (file.ownerUserID === managementUser?.userID) {
                        <div>
                          @if (managementUser?.isCustomerServiceUser() || managementUser?.isPartnerSuccessUser()) {
                            <div class="share-section">
                              <div class="share-action">
                                <label for="emailInput{{getElementId(file)}}">Share with:</label>
                                <input
                                  type="email"
                                  id="emailInput{{getElementId(file)}}"
                                  [(ngModel)]="file.newShareEmail"
                                  placeholder="Enter email" />
                                <button class="share-action-button" (click)="addSharedEmail(file.fileID || i)">Share</button>
                              </div>
                              @if (file.sharedUsers && file.sharedUsers.length) {
                                <div class="shared-users">
                                  @for (user of file.sharedUsers; track user; let j = $index) {
                                    <span class="shared-email">
                                      {{ user.firstName }} {{ user.lastName }} ({{ user.email }})
                                      <span class="remove-email" (click)="removeSharedEmail(file.fileID || i, j)">x</span>
                                    </span>
                                  }
                                </div>
                              }
                            </div>
                          } @else {
                            <p style="font-style: italic; color: gray;">
                              Uploaded files are automatically shared with FIL.
                            </p>
                          }
                        </div>
                      }
                      <!-- End Share Section -->
                    </div>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>
    }

    <!-- Upload Section with Drag and Drop -->
    <div class="upload-container"
      (dragover)="canUploadMoreFiles ? onDragOver($event) : $event.preventDefault()"
      (dragleave)="canUploadMoreFiles ? onDragLeave($event) : $event.preventDefault()"
      (drop)="canUploadMoreFiles ? onDrop($event) : $event.preventDefault()"
      [class.disabled]="!canUploadMoreFiles">
      @if (canUploadMoreFiles) {
        <p>Drag and drop files here or click the button below to upload files.</p>
      }
      @if (!canUploadMoreFiles) {
        <p class="upload-limit-message" style="color: red; font-weight: bold;">
          You have reached the maximum of 10 uploaded files. Please remove a file to upload more.
        </p>
      }
      <!-- Upload subheading for non allowed users -->
      @if (!managementUser?.isCustomerServiceUser() && !managementUser?.isPartnerSuccessUser()) {
        <p class="upload-subheading">
          <em style="color: gray;">Uploaded files are automatically shared with the WordFlight support team</em>
        </p>
      }
      @if (canUploadMoreFiles) {
        <button class="upload-button" (click)="fileInput.click()">Upload Files</button>
      }
      <input #fileInput type="file" multiple (change)="onFileSelected($event)" hidden [disabled]="!canUploadMoreFiles">
    </div>
  </div>
</div>
